/* istanbul ignore file */
import React from 'react';
import { batch } from 'react-redux';
import { useCurator } from '@noths/curator-web';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { isContentPreviewEnabled, NotFound } from '@noths/polaris-client-next-components';
import { DepartmentBanner } from '@noths/polaris-client-ribbons-design-system';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { InternalLinkGroups } from '@noths/polaris-internal-links';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { parse } from 'url';

import { adaptCategoryQueryContentToStore } from 'src/adapters/contentToStore/adaptCategoryQueryContentToStore';
import { adaptPageContentToStore } from 'src/adapters/contentToStore/adaptPageContentToStore';
import { adaptPageSettingsToStore } from 'src/adapters/contentToStore/adaptPageSettingsToStore';
import {
  adaptStoreToNextPageURL,
  adaptStoreToPreviousPageURL,
} from 'src/adapters/storeToPageURL/adaptStoreToPaginationURL';
import { adaptStoreToCategoryPageLinkedData } from 'src/adapters/storeToSEO/adaptStoreToCategoryPageLinkedData';
import type { CategoryListingProps } from 'src/components/layouts/CategoryListing/CategoryListing';
import { CategoryListingContainer } from 'src/components/layouts/CategoryListing/CategoryListingContainer';
import { isProd } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { ThunkSerializableError } from 'src/redux/products/thunks/thunkErrors';
import {
  CATEGORY_NOT_FOUND_ERROR,
  MISSING_CATEGORY_ID_ERROR,
  MISSING_LISTINGS_QUERY_ERROR,
  PRODUCTS_LIST_DATA_FETCHING_ERROR,
  serializeError,
} from 'src/redux/products/thunks/thunkErrors';
import { getInitialListingsControlsState } from 'src/redux/productsQuery/helpers/getInitialListingsControlsState';
import { urlPathToVertexPageCategory } from 'src/redux/productsQuery/helpers/pageCategories';
import { syncFromURL } from 'src/redux/productsQuery/thunks/syncFromURL';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore } from 'src/redux/store';
import { actions as userActions } from 'src/redux/user/slice';
import { getLDBreadcrumbListSchema } from 'src/seo/linkedData/getLDBreadcrumbListSchema';
import { getLDItemListSchema } from 'src/seo/linkedData/getLDItemListSchema';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchListingsControlsConfig } from 'src/services/contentstack/fetchListingsControlsConfig';
import { fetchPLPContent } from 'src/services/contentstack/fetchPLPContent';
import { fetchPLPSettings } from 'src/services/contentstack/fetchPLPSettings';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import { getBrowseServingConfig } from 'src/services/listings-api/servingConfigs';
import { fetchDeliveryEvent } from 'src/services/mononoth/fetchDeliveryEvent';
import { getInternalLinks } from 'src/services/similar-ai/getInternalLinks';
import { getServerSidePageEventObject } from 'src/tracking/events/pageEvent';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import { adaptBreadcrumbsLinks } from 'src/utils/adaptBreadcrumbsLinks';
import { buildCategoryPageTitle } from 'src/utils/buildCategoryPageTitle';
import { extractBasePath } from 'src/utils/extractBasePath';
import { getRobotsContent } from 'src/utils/getRobotsContent';
import { logger } from 'src/utils/serverLogger';

const Curator = dynamic(
  () =>
    import('@noths/curator-web').then(async (m) => {
      m.configureCurator({
        env: isProd() ? 'production' : 'qa',
        hideChatToggleForMobileViewport: true,
      });
      return m.Curator;
    }),
  { ssr: false },
);

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, req, res } = context;
  const { category, ...queryParams } = query;
  const preview = isContentPreviewEnabled(context.req);
  const featureFlags = getFeatureFlagsWithMatchers(req);
  const pathname = parse(req.url!).pathname!;
  const basePath = extractBasePath(pathname);
  const env = getContentstackEnvironment(preview);
  const healthCheck = req.headers['health-check'] === 'true';
  const { _ga: googleAnalyticsVisitorId, cognitoId, currency, delivery_zone } = req.cookies;
  const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
    parseLocaleSetCookieHeaders(res.getHeaders());
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);

  // Site chrome is deliberately absent here and fetched in the final wave
  // instead. Nothing on the happy path between this point and the props object
  // needs it, so leaving it in this barrier would hold the listings request
  // behind a call it does not depend on.
  const [content, settings, deliveryEvent, fetchedListingsControlsConfig] = await Promise.all([
    healthCheck
      ? import('src/services/contentstack/fixtures/healthCheckContentFixture').then(
          (file) => file.healthCheckContent,
        )
      : fetchPLPContent({ urlPath: basePath, env }),
    fetchPLPSettings({ env }),
    fetchDeliveryEvent(),
    fetchListingsControlsConfig(),
  ]);

  if (!content) {
    res.statusCode = StatusCode.NotFound;

    // This branch returns before the final wave, so it fetches its own copy.
    const [siteChromeContent] = await fetchGlobalContent({ featureFlags, urlPath: basePath });

    return {
      props: {
        paginationTagValues: [],
        siteChromeContent,
        updatedReduxState: {
          content: {
            error: 'NO_CONTENT_FOR_CATEGORY_PAGE',
          },
        },
      },
    };
  }

  const redirectToURL = content.redirect_to?.navigationConnection?.edges[0]?.node?.url || null;

  if (redirectToURL) {
    return {
      redirect: {
        destination: redirectToURL,
        permanent: false,
      },
    };
  }

  const breadcrumbLinks = adaptBreadcrumbsLinks(
    content.ancestorsConnection?.edges || [],
    content.heading,
    basePath,
  );

  const { facets, sortOptions } = getInitialListingsControlsState(fetchedListingsControlsConfig, {
    deliveryEvent: deliveryEvent && {
      date: deliveryEvent.last_deliver_by,
      name: deliveryEvent.name,
    },
  });

  let error;
  let canonicalFilters = null;

  try {
    canonicalFilters = adaptCategoryQueryContentToStore(content, featureFlags);
  } catch (err) {
    error = err as ThunkSerializableError;
  }

  const servingConfig = getBrowseServingConfig();

  const { dispatch, getState } = getStore(
    {
      router: { pathname: basePath, query: queryParams },
    },
    {
      content: {
        category: adaptPageContentToStore(content, basePath),
        ...(settings ? adaptPageSettingsToStore(settings, basePath) : {}),
      },
      navigation: {
        pageType: PageType.Category,
      },
      ...(error ? { products: { error: serializeError(error), pages: {} } } : {}),
      productsQuery: {
        ...(canonicalFilters && { canonicalFilters }),
        facets,
        page: 1,
        pageCategories: [urlPathToVertexPageCategory(basePath)],
        servingConfig,
        sortOptions,
        taxonomyAttributeAliases: [],
      },
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        cognitoID: cognitoId || null,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(syncFromURL(req.url!));
    dispatch(navigationActions.setPathname({ url: basePath, host: req.headers.host ?? '' }));
    dispatch(userActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
    dispatch(userActions.setFeatureFlags(featureFlags));
  });

  const [internalLinks, , [siteChromeContent]] = await Promise.all([
    getInternalLinks(basePath),
    !error ? dispatch(requestProducts({ healthCheck })) : null,
    fetchGlobalContent({ featureFlags, urlPath: basePath }),
  ]);

  const state = getState();
  const { products } = state;

  if (products.error) {
    switch (products.error.name) {
      case CATEGORY_NOT_FOUND_ERROR.name:
      case PRODUCTS_LIST_DATA_FETCHING_ERROR.name:
        res.statusCode = StatusCode.NotFound;
        break;
      case MISSING_CATEGORY_ID_ERROR.name:
        res.statusCode = StatusCode.InternalServerError;
        logger.error('category ID missing for category listing page');
        break;
      case MISSING_LISTINGS_QUERY_ERROR.name:
        res.statusCode = StatusCode.InternalServerError;
        logger.error('listings_query missing for category listing page');
        break;
      default:
        res.statusCode = StatusCode.InternalServerError;
        res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
        break;
    }
  }

  const pageEventData = getServerSidePageEventObject(
    publicRuntimeConfig.version,
    publicRuntimeConfig.appVersion,
    state,
  );

  const internalLinkGroups = internalLinks as InternalLinkGroups;
  const previousPageURL = adaptStoreToPreviousPageURL(state);
  const nextPageURL = adaptStoreToNextPageURL(state);
  const inspirationLinks = state.content.category?.inspirationPageLinks || [];
  const bottomLinks = inspirationLinks.concat(internalLinkGroups?.bottom || []);

  const props: Partial<CategoryListingProps> = {
    featureFlags,
    preview,
    siteChromeContent,
    stickyTopBanner: siteChromeContent ? siteChromeContent.topBannerMessages.length > 0 : false,
    // TODO: update buildCategoryPageTitle to not expect last breadcrumb entry to be current page
    pageTitle: buildCategoryPageTitle({
      heading: state.content.category!.heading,
      breadcrumbLinks,
      pageTitleOverride: state.content.category!.pageTitleOverride,
    }),
    paginationTagValues: [previousPageURL, nextPageURL],
    pageEventData,
    ...selectPagePropsForClientHydration(state),
    topLinks: internalLinkGroups?.top || [],
    bottomLinks,
    breadcrumbLinks,
    linkedDataSchema: adaptStoreToCategoryPageLinkedData(state),
  };

  return {
    props,
  };
};

const CategoryPage = (props: CategoryListingProps) => {
  const { content, productsQuery } = props.updatedReduxState as ReduxApplicationState;
  const [prevLinkHref, nextLinkHref] = props.paginationTagValues || [];
  const robotsContent = getRobotsContent(content.category?.indexable, productsQuery?.page || 1);

  const { handleToggle, isCuratorOpen, redirectToPDP } = useCurator();

  return (
    <>
      <Head>
        <title>{props.pageTitle}</title>
        {content.category?.metaTagDescription && (
          <meta content={content.category.metaTagDescription} name="description" />
        )}
        {content.category?.canonicalTag && (
          <link href={content.category!.canonicalTag} rel="canonical" />
        )}
        {robotsContent && <meta content={robotsContent} name="robots" />}
        {prevLinkHref && <link href={prevLinkHref} rel="prev" />}
        {nextLinkHref && <link href={nextLinkHref} rel="next" />}
        {props.breadcrumbLinks && (
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getLDBreadcrumbListSchema(props.breadcrumbLinks)),
            }}
            type="application/ld+json"
          />
        )}
        {props.linkedDataSchema && (
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getLDItemListSchema(props.linkedDataSchema)),
            }}
            type="application/ld+json"
          />
        )}
      </Head>
      {content.departmentBanner && <DepartmentBanner {...content.departmentBanner} />}
      {content.error ? <NotFound /> : <CategoryListingContainer {...props} />}
      <Curator
        isOpen={isCuratorOpen}
        onProductClick={redirectToPDP}
        onToggle={handleToggle}
        showFavourties
      />
    </>
  );
};

export default CategoryPage;
